import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { SearchIcon } from "../components/icons";
import domScrollTo from "../utils/domScrollTo";
import ProductsList from "../components/ProductsList";
import { setFilterByContent } from "../ducks/searchSlice";
import { MENU_SEARCH_INPUT_ID } from "./BasicSearch";
import useMenuCatalog from "../hooks/useMenuCatalog";

// Depois de um toque no chip, o scroll suave passa por outros grupos; durante
// esse tempo o scroll-spy fica mudo para o chip não piscar.
const PROGRAMMATIC_SCROLL_MS = 800;

export default function GroupedProductsView() {
  const dispatch = useDispatch();
  const search = useSelector((state) => state.search);
  const {
    productsByGroup: filteredProductsByGroup,
    visibleGroups,
    onProductSelect,
  } = useMenuCatalog(search.content);
  const visibleGroupIds = visibleGroups.map((g) => g.id).join(",");

  const navRef = React.useRef(null);
  const { activeGroupId, goToGroup } = useScrollSpy(visibleGroupIds, navRef);

  const focusSearch = () => {
    domScrollTo(0);
    // o foco só depois do scroll, senão o teclado abre com a busca fora da
    // tela
    setTimeout(() => {
      document.getElementById(MENU_SEARCH_INPUT_ID)?.focus();
    }, 350);
  };

  return (
    <div className="menu-sections">
      {visibleGroups.length > 0 && (
        <nav className="menu-bar" aria-label="Categorias" ref={navRef}>
          <button
            type="button"
            className="icon-button menu-bar__search"
            onClick={focusSearch}
            aria-label="Buscar no cardápio"
          >
            <SearchIcon />
          </button>
          <ChipsTrack activeId={activeGroupId}>
            {visibleGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                data-group-id={group.id}
                className={`menu-chip${
                  group.id === activeGroupId ? " is-active" : ""
                }`}
                aria-current={group.id === activeGroupId ? "true" : undefined}
                onClick={() => goToGroup(group.id)}
              >
                {group.name}
              </button>
            ))}
          </ChipsTrack>
        </nav>
      )}
      {visibleGroups.length === 0 && search.content && (
        <div className="empty-state">
          <p className="empty-state__title">
            Nada encontrado para “{search.content.trim()}”
          </p>
          <p>Confira a grafia ou tente outro termo.</p>
          <button
            type="button"
            className="btn btn-outline-shop"
            onClick={() => dispatch(setFilterByContent({ content: "" }))}
          >
            Limpar busca
          </button>
        </div>
      )}
      {visibleGroups.map((group) => (
        <section
          key={group.id}
          id={groupSectionId(group.id)}
          data-group-id={group.id}
          className="menu-section"
          aria-labelledby={`${groupSectionId(group.id)}-title`}
        >
          <h2
            id={`${groupSectionId(group.id)}-title`}
            className="section-title"
          >
            {group.name}
          </h2>
          <ProductsList
            products={filteredProductsByGroup[group.id]}
            onProductSelect={onProductSelect}
          />
        </section>
      ))}
    </div>
  );
}

const groupSectionId = (groupId) => `grupo-${groupId}`;

/** Faixa de chips que rola sozinha para manter o ativo à vista. */
function ChipsTrack({ activeId, children }) {
  const trackRef = React.useRef(null);

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || activeId === null) return;
    const chip = track.querySelector(`[data-group-id="${activeId}"]`);
    if (!chip) return;

    const left = chip.offsetLeft - (track.clientWidth - chip.clientWidth) / 2;
    try {
      track.scrollTo({ left, behavior: "smooth" });
    } catch {
      // WebView sem scrollTo com opções
      track.scrollLeft = left;
    }
  }, [activeId]);

  return (
    <div className="menu-bar__chips" ref={trackRef}>
      {children}
    </div>
  );
}

/**
 * Qual grupo está na tela, para destacar o chip. Um grupo fica ativo quando
 * cruza a faixa logo abaixo da barra fixa.
 *
 * Usa IntersectionObserver em vez de medir offsetTop no scroll: as posições
 * mudam quando as fotos carregam ou a busca filtra, e medidas feitas uma vez
 * ficavam defasadas. Sem IntersectionObserver (WebView muito antigo), os chips
 * funcionam, só não acompanham a rolagem.
 */
function useScrollSpy(visibleGroupIds, navRef) {
  const [activeGroupId, setActiveGroupId] = React.useState(null);
  const lockUntilRef = React.useRef(0);

  React.useEffect(() => {
    const ids = visibleGroupIds ? visibleGroupIds.split(",").map(Number) : [];
    setActiveGroupId((current) => (ids.includes(current) ? current : null));
    if (!ids.length || typeof IntersectionObserver === "undefined") return;

    const barHeight = stickyTopHeight(navRef, 56);
    const intersecting = new Set();

    const pick = () => {
      if (Date.now() < lockUntilRef.current) return;
      if (isWindowScrollAtEnd()) {
        setActiveGroupId(ids[ids.length - 1]);
        return;
      }
      const first = ids.find((id) => intersecting.has(id));
      if (first !== undefined) {
        setActiveGroupId(first);
      } else if (window.pageYOffset < barHeight) {
        setActiveGroupId(null);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = Number(entry.target.dataset.groupId);
          if (entry.isIntersecting) intersecting.add(id);
          else intersecting.delete(id);
        });
        pick();
      },
      // uma faixa do fim da barra até 35% da tela
      { rootMargin: `-${barHeight + 1}px 0px -65% 0px` },
    );

    ids.forEach((id) => {
      const section = document.getElementById(groupSectionId(id));
      if (section) observer.observe(section);
    });

    // o último grupo, se for curto, nunca chega à faixa: no fim da página ele
    // é o ativo
    const onScroll = () => {
      if (isWindowScrollAtEnd()) pick();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [visibleGroupIds, navRef]);

  const goToGroup = (groupId) => {
    const section = document.getElementById(groupSectionId(groupId));
    if (!section) return;

    const barHeight = stickyTopHeight(navRef, 0);
    const top =
      section.getBoundingClientRect().top + window.pageYOffset - barHeight;
    lockUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
    setActiveGroupId(groupId);
    domScrollTo(top);
  };

  return { activeGroupId, goToGroup };
}

// O que fica preso no topo da tela: a barra de categorias e, se houver, a
// faixa do pedido em andamento (StatusButton).
function stickyTopHeight(navRef, fallback) {
  const bar = navRef.current ? navRef.current.offsetHeight : fallback;
  const banner = document.querySelector(".order-banner");
  return bar + (banner ? banner.offsetHeight : 0);
}

const isWindowScrollAtEnd = () =>
  document.documentElement.scrollHeight -
    document.documentElement.scrollTop -
    40 <=
  document.documentElement.clientHeight;
